import { Box, Button, GridItem, Stack } from '@chakra-ui/react'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import 'swiper/swiper-bundle.css'

import UserQ from '@/queries/UserQ'
import { useAppContext } from '@/client/context/app'
import { useUser } from '@/client/context/user'
import type { Query, SQLFragment } from '@/client/query'
import { getUserUrl } from '@/client/services/url'

import ShwGrid from '@/components/Layout/ShwGrid'
import ShwLink from '@/components/ShwLink'
import LinkableDiv from '@/components/UI/LinkableDiv'

import ProfileAbout from './ProfileAbout'
import ProfileAopsUsername from './ProfileAopsUsername'
import ProfileBadges from './ProfileBadges'
import ProfileBlogArticles from './ProfileBlogArticles'
import ProfileCertifications from './ProfileCertifications'
import ProfileConnections from './ProfileConnections'
import ProfileFeaturedFeedback from './ProfileFeaturedFeedback'
import ProfileKhanAcademyUsername from './ProfileKhanAcademyUsername'
import ProfileStats from './ProfileStats'
import ProfileTutorNetwork from './ProfileTutorNetwork'
import ProfileTutorTree from './ProfileTutorTree'
import ProfileUpcomingSessions from './ProfileUpcomingSessions'

const BADGES_LIMIT = 8

function ProfileOverviewTab({
  user,
  userIdQuery,
  upcomingSeries,
}: {
  user: Query
  userIdQuery: SQLFragment<number>
  upcomingSeries: Query[]
}): JSX.Element {
  const currentUser = useUser()
  const appContext = useAppContext()

  const userId = user(UserQ.id) ?? 0
  const username = user(UserQ.username)

  return (
    <Box>
      <ShwGrid>
        <GridItem colSpan={[12, 12, 6, 4]}>
          <Stack direction="column" spacing="32px">
            <ProfileAbout user={user} />
            <ProfileStats user={user} />
            <ProfileCertifications user={user} />
            <Box>
              <LinkableDiv id="connections" />
              <ProfileConnections user={user} userIdQuery={userIdQuery} />
            </Box>
            <Stack direction="column" spacing="24px">
              <ProfileAopsUsername user={user} />
              <ProfileKhanAcademyUsername user={user} />
              {appContext.prepass || !currentUser.isLoggedOut ? (
                <>
                  <ProfileTutorNetwork user={user} />
                  <ProfileTutorTree user={user} />
                </>
              ) : null}
            </Stack>
          </Stack>
        </GridItem>
        <GridItem colSpan={[12, 12, 6, 8]}>
          <Stack direction="column" spacing="32px">
            <ProfileUpcomingSessions user={user} upcomingSeries={upcomingSeries} />
            <ProfileFeaturedFeedback
              user={user}
              compact
              hideIfEmpty
              ButtonComponent={() => (
                <ShwLink href={getUserUrl({ userId, username, path: 'feedback' })}>
                  <Button variant="ghost" rightIcon={<FontAwesomeIcon icon={faChevronRight} />}>
                    See All
                  </Button>
                </ShwLink>
              )}
            />
            <ProfileBadges
              user={user}
              limit={BADGES_LIMIT}
              ButtonComponent={() => (
                <ShwLink href={getUserUrl({ userId, username, path: 'achievements' })}>
                  <Button variant="ghost" rightIcon={<FontAwesomeIcon icon={faChevronRight} />}>
                    See All
                  </Button>
                </ShwLink>
              )}
            />
            <ProfileBlogArticles user={user} />
          </Stack>
        </GridItem>
      </ShwGrid>
    </Box>
  )
}

export default ProfileOverviewTab
